"use client";

/**
 * One-time install offer shown to mobile users post sign-in.
 *
 * Intent: surface the home-screen install nudge once the user has
 * actually committed to the product (signed in / verified email)
 * rather than ambushing anonymous first-time visitors who haven't
 * decided yet. Bottom sheet (not a modal) so the page underneath
 * is still scannable.
 *
 * Shown iff: mobile viewport + signed-in user + not already
 * installed + not previously dismissed + the post-signup welcome
 * modal isn't currently in flight (we suppress while
 * ``?welcome=1`` is in the URL so the install sheet doesn't grab
 * the screen before WelcomeModal) + its turn in the interruption
 * budget (lib/floating-slot): one offer per session, never on its
 * first page view, never over a result or a drawer. The welcome
 * card uses up its session, so the sheet waits for the next one.
 */
import { useEffect, useState } from "react";
import { useInterruption } from "@/lib/floating-slot";
import { createPortal } from "react-dom";
import { useSearchParams } from "next/navigation";
import Link from "@/lib/localized-link";
import { Download } from "lucide-react";
import { useT } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import { isNativeApp } from "@/lib/native-app";
import {
  useDismissalFlag,
  useInstallPrompt,
  useIsMobileViewport,
} from "@/lib/use-install-prompt";
import { useSheetDrag } from "@/lib/use-sheet-drag";

const DISMISSAL_KEY = "menzil-install-firstvisit-dismissed";

export default function FirstVisitInstallSheet() {
  // Portals need a DOM: nothing renders on the server pass.
  const [portalReady, setPortalReady] = useState(false);
  useEffect(() => setPortalReady(true), []);
  const t = useT();
  const isMobile = useIsMobileViewport();
  const { canInstall, installed, promptInstall } = useInstallPrompt();
  const [seen, markSeen] = useDismissalFlag(DISMISSAL_KEY);
  const { user, loading: authLoading } = useAuth();
  const searchParams = useSearchParams();
  // ``?welcome=1`` is set by the Google OAuth callback and means a
  // WelcomeModal is about to render. The install sheet would stack
  // on top of that, hiding the modal CTA - wait until the home page
  // has consumed the param (router.replace strips it) before firing.
  const welcomeInFlight = searchParams?.get("welcome") === "1";

  // Swipe down on the handle to dismiss (same gesture as every other
  // mobile sheet). Hooks must run unconditionally - before the early
  // returns below.
  const { offset, dragging, handleProps, panelRef } = useSheetDrag(markSeen);

  // Inside the native app there is nothing to install - the sheet
  // would be advertising the product to someone already using it.
  // Gate on auth: only signed-in users see the post-welcome install
  // sheet. Anonymous visitors get the home-page inline promo +
  // post-analysis card instead - both quieter than this intercepting
  // surface.
  const ready = portalReady && !isNativeApp() && isMobile && !installed && !seen
    && !authLoading && !!user && !welcomeInFlight;
  const turn = useInterruption("install", ready);

  // Small reveal delay once it is the sheet's turn, so the page's own
  // content paints first and the sheet slides in ~500ms later. Feels
  // less ambushy than mounting instantly.
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (!turn) { setRevealed(false); return undefined; }
    const timer = setTimeout(() => setRevealed(true), 500);
    return () => clearTimeout(timer);
  }, [turn]);

  if (!turn || !revealed) return null;

  const handleInstall = async () => {
    if (!canInstall) return;
    const outcome = await promptInstall();
    // Either "accepted" or "dismissed" - in both cases the user now
    // knows the offer exists, so don't re-show next visit.
    if (outcome !== "unavailable") markSeen();
  };

  return createPortal(
    <>
      {/* Dimmed backdrop - tappable to dismiss, feels lighter than
          a full modal. */}
      <div
        role="presentation"
        onClick={markSeen}
        className="fixed inset-0 z-[1110] bg-black/30 backdrop-blur-[1px] animate-in fade-in duration-300"
      />
      <div ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pwa-fv-title"
        style={{
          transform: `translateY(${offset}px)`,
          transition: dragging ? "none" : "transform 200ms ease-out",
        }}
        className="fixed inset-x-0 bottom-0 z-[1120] rounded-t-3xl bg-white p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-2xl animate-in slide-in-from-bottom duration-300"
      >
        {/* Drag handle - swipe down to dismiss. */}
        <div
          {...handleProps}
          role="button"
          tabIndex={0}
          aria-label={t("a11y.drag_close")}
          className="-mt-6 -mx-6 mb-2 pt-3 pb-2 flex justify-center touch-none cursor-grab active:cursor-grabbing"
        >
          <div className="h-1.5 w-10 rounded-full bg-gray-300" />
        </div>

        {/* No X: the sheet's own "Sonra" button says the same (owner,
            2026-10-23: no visible X on phone sheets). */}

        {/* The mark and the title on one line, the sentence under them at
            full width, one wide button and "later" as text: three
            buttons in a row and a sentence beside the mark were squeezed
            on phones (owner, 2026-10-11). */}
        <div className="flex items-center gap-3 pr-8">
          {/* Use the shipped app icon directly so this sheet matches
              what the user will see on their home screen after install.
              Vector logo scales sharply at every device DPR. */}
          <img
            src="/logo.svg"
            alt=""
            aria-hidden="true"
            className="h-11 w-11 shrink-0 rounded-2xl shadow-sm"
          />
          <h2 id="pwa-fv-title" className="text-base font-semibold leading-snug text-gray-900">
            {t("pwa.firstvisit.title")}
          </h2>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-gray-600">
          {t("pwa.firstvisit.body")}
        </p>

        <div className="mt-4 flex flex-col gap-2">
          {canInstall ? (
            <button
              type="button"
              onClick={handleInstall}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              <Download className="h-4 w-4" />
              {t("pwa.install.cta")}
            </button>
          ) : (
            <Link
              href="/install"
              onClick={markSeen}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              {t("pwa.firstvisit.showhow")}
            </Link>
          )}
          <button
            type="button"
            onClick={markSeen}
            className="rounded-xl px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            {t("pwa.install.later")}
          </button>
        </div>
      </div>
    </>
  , document.body);
}
